import {
  IconButton,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { Box, Button, ButtonBase } from "@mui/material";
import { Switch, TextField, Typography } from "@mui/material";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { useContext, useEffect, useState } from "react";
import React from "react";
import CloseIcon from "@mui/icons-material/Close";
import { styles } from "./styles";
import {
  AuthContext,
  AuthContextInterface,
  UserDetailsContext,
  UserDetailsContextInterface,
} from "../../contexts";
import { createAndCopyShareUrl } from "../TrackComponents/TrackMenu/trackMenuHelper";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { ShareDetailsModalProps } from "./types";
import { Modal } from "reactstrap";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-toastify";
import { getShareUrl } from "../../services/shareService";
import { PostShareResponse, Share, ShareType } from "../../proto/api_pb";
import { NUMBER_REGEX } from "../constants";
const label = { inputProps: { "aria-label": "Switch demo" } };

export const ShareDetailsModal: React.FC<ShareDetailsModalProps> = ({
  isShareDetailsModalOpen,
  setIsShareDetailsModalOpen,
  id,
  title,
  shareType,
  shareForUpdate,
  isUpdateModal,
  getSharesList,
}) => {
  const [shareLink, setShareLink] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [isAllowDownload, setIsisAllowDownload] = useState<boolean>(true);
  const [maxViews, setMaxViews] = useState<any>(10);
  const [isShareLinkAvailable, setIsShareLinkAvailable] =
    useState<boolean>(false);
  const [isValidMaxViews, setIsValidMaxViews] = useState<boolean>(true);
  const [isAllowUnlimitedViews, setIsAllowUnlimitedViews] =
    useState<boolean>(false);
  const [selectValue, setSelectValue] = useState(1);
  const { userId } = useContext<AuthContextInterface>(AuthContext);
  const { getValidAccessToken } = useGetValidAccessToken();
  const { isEnterpriseAccount, enterprise } =
    useContext<UserDetailsContextInterface>(UserDetailsContext);

  const calculateExpirationTimestamp = (hours: number): number => {
    const currentTime = new Date();
    let expirationTimestamp: number;

    switch (hours) {
      case 0: // 1 year
        expirationTimestamp = currentTime.getTime() + 365 * 24 * 60 * 60 * 1000;
        break;
      case 1: // 1 Hour
        expirationTimestamp = currentTime.getTime() + 1 * 60 * 60 * 1000;
        break;
      case 12: // 12 Hours
        expirationTimestamp = currentTime.getTime() + 12 * 60 * 60 * 1000;
        break;
      case 24: // 1 Day
        expirationTimestamp = currentTime.getTime() + 24 * 60 * 60 * 1000;
        break;
      case 168: // 1 Week
        expirationTimestamp = currentTime.getTime() + 7 * 24 * 60 * 60 * 1000;
        break;
      default: // Default to 1 Hour
        expirationTimestamp = currentTime.getTime() + 1 * 60 * 60 * 1000;
        break;
    }

    return expirationTimestamp;
  };

  const handleUpdate = async () => {
    if (shareForUpdate) {
      const share = new Share();
      const expiryTime = calculateExpirationTimestamp(selectValue);
      share.setId(shareForUpdate.getId());
      share.setObjectId(shareForUpdate.getObjectId());
      share.setOwnerId(userId);
      share.setExpiryTimestamp(expiryTime);
      share.setMaxViews(maxViews);
      share.setAllowDownload(isAllowDownload);
      share.setUnlimitedViews(isAllowUnlimitedViews);
      share.setCreatedTimestamp(shareForUpdate.getCreatedTimestamp());
      share.setShareType(shareForUpdate.getShareType());
      share.setShareState(shareForUpdate.getShareState());
      share.setTrackTitle(title ?? "");
      share.setImpressionCount(shareForUpdate.getImpressionCount());
      const response = await getShareUrl(
        await getValidAccessToken(),
        userId,
        share,
        isEnterpriseAccount ? enterprise?.id : undefined
      );
      if (response instanceof PostShareResponse) {
        const updatedShare = response.getShare();
        const newId = updatedShare?.getId() ?? shareForUpdate.getId();
        const shareTitle = updatedShare?.getTrackTitle() ?? title ?? "";
        const type = shareForUpdate.getShareType();
        let newUrl = "";
        if (type === ShareType.SHARE_TYPE_PROJECT) {
          newUrl = `${window.location.origin}/share/project/${shareTitle}/${newId}`;
        } else if (type === ShareType.SHARE_TYPE_FOLDER) {
          newUrl = `${window.location.origin}/share/${shareTitle}/${newId}`;
        } else if (type === ShareType.SHARE_TYPE_PROJECTS_COLLECTION) {
          newUrl = `${window.location.origin}/share/projects/${newId}`;
        } else {
          newUrl = `${window.location.origin}/share/${newId}`;
        }

        if (newId !== shareForUpdate.getId()) {
          setShareLink(newUrl);
          navigator.clipboard.writeText(newUrl).catch(() => {});
          toast.info("New link copied to clipboard — the old link is no longer valid.");
        } else {
          toast.success("Link updated successfully.");
          setIsShareDetailsModalOpen(false);
        }

        if (getSharesList) getSharesList();
      }
    }
  };

  const handleGenerateLink = async () => {
    setLoading(true);
    const linkExpiryTime = calculateExpirationTimestamp(selectValue);
    if (id && NUMBER_REGEX.test(maxViews)) {
      const shareUrl = await createAndCopyShareUrl(
        id,
        userId,
        await getValidAccessToken(),
        shareType,
        linkExpiryTime,
        maxViews,
        isAllowDownload,
        isAllowUnlimitedViews,
        title!,
        isEnterpriseAccount ? enterprise?.id : undefined
      );
      if (shareUrl !== undefined) {
        setShareLink(shareUrl);
        setLoading(false);
        setIsShareLinkAvailable(true);
      }
    } else {
      toast.warn("Please enter valid number for max views");
    }
  };

  const handleCopyShareUrl = () => {
    if (shareLink) {
      navigator.clipboard
        .writeText(shareLink)
        .then(() => {
          console.log("URL successfully copied to clipboard");
          toast.info("URL copied");
        })
        .catch((err) => {
          console.error("Unable to copy URL to clipboard", err);
        });
    }
  };

  const handleExpiryChange = (event: SelectChangeEvent<number>) => {
    setSelectValue(Number(event.target.value));
  };

  const handleClose = () => {
    setShareLink("");
    setLoading(false);
    setIsisAllowDownload(false);
    setMaxViews(10);
    setIsShareLinkAvailable(false);
    setIsAllowUnlimitedViews(false);
    setSelectValue(1);
    setIsShareDetailsModalOpen(false);
  };

  useEffect(() => {
    if (shareForUpdate) {
      setIsisAllowDownload(shareForUpdate.getAllowDownload());
      setMaxViews(shareForUpdate.getMaxViews());
      setIsAllowUnlimitedViews(shareForUpdate.getUnlimitedViews());
    }
  }, [shareForUpdate]);

  useEffect(() => {
    if (maxViews < 1) {
      setIsValidMaxViews(false);
    } else {
      setIsValidMaxViews(true);
    }
    if (isAllowUnlimitedViews) {
      setIsValidMaxViews(true);
    }
  }, [isAllowUnlimitedViews, maxViews]);

  return (
    <>
      <Modal
        isOpen={isShareDetailsModalOpen}
        centered
        onClosed={() => setIsShareDetailsModalOpen(false)}
      >
        <Box sx={styles.modalContainer}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <DialogTitle sx={{ width: "90%", color: "white" }}>
              {!isUpdateModal ? `Share ${title}` : `Update`}
            </DialogTitle>
            <ButtonBase onClick={handleClose}>
              <CloseIcon sx={{ color: "white" }} />
            </ButtonBase>
          </Box>
          <hr style={{ marginTop: -5 }} />
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <Box>
                <Typography sx={{ color: "white" }}>Allow Download</Typography>
                <Switch
                  disabled={isShareLinkAvailable}
                  checked={isAllowDownload}
                  onChange={() => setIsisAllowDownload(!isAllowDownload)}
                  {...label}
                />
              </Box>
              <Box>
                <Typography sx={{ color: "white" }}>Allow Unlimited Views</Typography>
                <Switch
                  disabled={isShareLinkAvailable}
                  checked={isAllowUnlimitedViews}
                  onChange={() => {
                    setIsAllowUnlimitedViews(!isAllowUnlimitedViews);
                  }}
                  {...label}
                />
              </Box>
              {!isAllowUnlimitedViews && (
                <Box>
                  <Typography sx={{ color: "white" }}>Set Max Views</Typography>
                  <TextField
                    error={!isValidMaxViews}
                    disabled={isShareLinkAvailable || isAllowUnlimitedViews}
                    type="number"
                    value={maxViews}
                    inputProps={{
                      pattern: "[0-9]*",
                    }}
                    sx={{ width: "100%" }}
                    onChange={(e) => {
                      const numberRegex = /^\d+$/;
                      if (numberRegex.test(e.target.value)) {
                        setMaxViews(Number(e.target.value));
                      } else {
                        setMaxViews(Number(e.target.value));
                      }
                    }}
                  />
                  {!isValidMaxViews && (
                    <p style={{ color: "red", fontSize: "14px" }}>
                      Please enter valid number (greater than 0)
                    </p>
                  )}
                </Box>
              )}
              <Box>
                <Typography sx={{ color: "white" }}>Link Expiry Time</Typography>
                <Select
                  disabled={isShareLinkAvailable}
                  sx={{ marginTop: "10px" }}
                  fullWidth
                  value={selectValue}
                  onChange={(e) => handleExpiryChange(e)}
                >
                  <MenuItem value={1}>1 Hour</MenuItem>
                  <MenuItem value={12}>12 Hours</MenuItem>
                  <MenuItem value={24}>1 Day</MenuItem>
                  <MenuItem value={168}>1 Week</MenuItem>
                  <MenuItem value={0}>1 Year</MenuItem>
                </Select>
              </Box>
            </Box>
            {shareLink && (
              <Box sx={{ marginTop: "20px", display: "flex" }}>
                <TextField
                  fullWidth
                  value={shareLink}
                  disabled={isShareLinkAvailable}
                  InputProps={{
                    endAdornment: (
                      <IconButton onClick={handleCopyShareUrl} edge="end">
                        <ContentCopyIcon />
                      </IconButton>
                    ),
                  }}
                />
              </Box>
            )}
            {shareLink && (
              <Typography sx={{ marginTop: "5px" }}>
                Note: Share settings can be updated from{" "}
                <Link href="/share-management">share management page</Link>
              </Typography>
            )}
            <Box sx={styles.buttonContainer}>
              {!isUpdateModal && (
                <Button
                  style={{ width: "200px" }}
                  onClick={() => {
                    if (isShareLinkAvailable) {
                      handleCopyShareUrl();
                    } else {
                      handleGenerateLink();
                    }
                  }}
                  sx={styles.buttons}
                >
                  {loading ? (
                    <CircularProgress sx={{ color: "black" }} size={"16px"} />
                  ) : isShareLinkAvailable ? (
                    "Copy URL"
                  ) : (
                    "Create share"
                  )}
                </Button>
              )}
              {isUpdateModal && (
                <Button
                  disabled={!isValidMaxViews}
                  onClick={handleUpdate}
                  sx={styles.buttons}
                  style={{ width: "200px" }}
                >
                  Update
                </Button>
              )}
            </Box>
          </DialogContent>
        </Box>
      </Modal>
    </>
  );
};
